import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Layers, ChevronDown, ChevronRight, Play, Search, Shield, Wrench, FileText,
  ClipboardCheck, ScanLine, Package, Globe, CheckCircle2, ExternalLink,
  Loader2, ArrowRight, Sparkles, Link2, AlertTriangle, Clock, Cpu,
  Upload, BarChart3, Mail, CalendarCheck, Building2, Eye, Film, Download, MonitorPlay,
  Copy, Check, Share2
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── Demo Steps Data ────────────────────────────────────────────────── */

const DEMO_STEPS = [
  {
    id: 'intake',
    title: 'Smart Intake',
    subtitle: 'Parse project documents from Day 1',
    icon: Upload,
    color: 'cyan',
    description: 'Upload sub lists, RFI logs, drawing sets, and change orders the moment they arrive. AI extracts equipment tags, model numbers, and change impacts using hybrid text+image analysis — all with real-time progress tracking.',
    features: ['Sub List Parser', 'RFI Log Parser', 'Hybrid Drawing Extraction', 'Change Order Tracker', 'SSE Progress Bars'],
    preview: 'intake',
  },
  {
    id: 'drawings',
    title: 'Drawing Intelligence',
    subtitle: 'Floor plans → AI-detected rooms → CAD export',
    icon: ScanLine,
    color: 'blue',
    description: 'Upload a floor plan PDF. Computer vision detects every room polygon, then GPT-4o classifies each space. Review in a CAD-quality editor with snap-to-wall precision. Export clean DXF or annotated PDF.',
    features: ['CV Polygon Detection', 'GPT-4o Classification', 'Snap-to-Wall Editor', 'Confidence Scoring', 'DXF/PDF Export'],
    preview: 'drawings',
  },
  {
    id: 'closeout',
    title: 'Closeout SOP',
    subtitle: 'G703 → divisions → AI filing → gap reports → chase emails',
    icon: ClipboardCheck,
    color: 'orange',
    description: 'Import the G703 Schedule of Values — AI reads the PDF directly. Auto-generates every CSI trade division with 9 standard folders. The Applicability Wizard marks what\'s required per trade. AI classifies and files every uploaded document automatically.',
    features: ['G703 AI Reader', 'AI Document Classification', '9-Folder CBC Structure', 'Gap Reports', 'Chase Email Generation', 'Quality Gates'],
    preview: 'closeout',
  },
  {
    id: 'equipment',
    title: 'Equipment Registry',
    subtitle: 'AI scans your docs and builds a complete asset list',
    icon: Cpu,
    color: 'violet',
    description: 'AI reads the project\'s division structure and drawings to extract every piece of equipment — make, model, type, CSI code, location, and quantity. No manual data entry. The equipment list is the foundation for everything that follows.',
    features: ['AI Equipment Extraction', 'Make/Model Detection', 'CSI Code Mapping', 'Drawing Tag Parsing', 'Batch Processing'],
    preview: 'equipment',
  },
  {
    id: 'warranty',
    title: 'Live AI Document Finder',
    subtitle: 'The showstopper — real OEM manuals, warranty terms, PM schedules, spare parts, vendor links',
    icon: Globe,
    color: 'emerald',
    description: 'This is what sets FMiQ Intel apart. Pick any equipment below and watch the AI search Google for real manufacturer documentation, verify every URL, extract warranty terms, generate a complete PM program, identify critical spare parts, and link vendor procurement — all in one call.',
    features: ['Google Search (Serper)', 'GPT-4o URL Verification', 'OEM Manual PDFs', 'Warranty Terms', 'PM Schedule', 'Critical Spare Parts', 'Vendor Procurement'],
    preview: 'warranty',
    interactive: true,
  },
  {
    id: 'handoff',
    title: 'Client Handoff Export',
    subtitle: 'PDF + Excel + ZIP — turnover-ready in one click',
    icon: Package,
    color: 'amber',
    description: 'Generate a complete turnover package: PDF report with cover sheet, asset registry, warranty schedule, and gap analysis. Excel workbook structured for direct CAFM/IWMS import (Archibus, FM:Systems, Planon). ZIP bundles everything with manifests.',
    features: ['PDF Turnover Report', 'IWMS Import Workbook', 'Complete ZIP Package', 'Export Preview', 'Gap Analysis'],
    preview: 'handoff',
  },
];

const PRESET_EQUIPMENT = [
  { label: 'Greenheck SP-A50 Exhaust Fan', make: 'Greenheck', model: 'SP-A50', type: 'Exhaust Fan' },
  { label: 'Kohler K-4325 Water Closet', make: 'Kohler', model: 'K-4325', type: 'Water Closet' },
  { label: 'Trane XR15 Heat Pump', make: 'Trane', model: 'XR15', type: 'Heat Pump' },
  { label: 'Lithonia 2BLT4 LED Troffer', make: 'Lithonia', model: '2BLT4', type: 'LED Troffer' },
];

const colorMap = {
  cyan: { bg: 'bg-cyan-950/20', border: 'border-cyan-900/40', text: 'text-cyan-400', accent: 'bg-cyan-500', dot: 'bg-cyan-400', pill: 'bg-cyan-950/30 border-cyan-900/30 text-cyan-300' },
  blue: { bg: 'bg-blue-950/20', border: 'border-blue-900/40', text: 'text-blue-400', accent: 'bg-blue-500', dot: 'bg-blue-400', pill: 'bg-blue-950/30 border-blue-900/30 text-blue-300' },
  orange: { bg: 'bg-orange-950/20', border: 'border-primary/30/40', text: 'text-primary', accent: 'bg-primary', dot: 'bg-amber-400', pill: 'bg-primary/10 border-primary/30 text-primary' },
  violet: { bg: 'bg-violet-950/20', border: 'border-violet-900/40', text: 'text-violet-400', accent: 'bg-violet-500', dot: 'bg-violet-400', pill: 'bg-violet-950/30 border-violet-900/30 text-violet-300' },
  emerald: { bg: 'bg-emerald-950/20', border: 'border-emerald-900/40', text: 'text-emerald-400', accent: 'bg-emerald-500', dot: 'bg-emerald-400', pill: 'bg-emerald-950/30 border-emerald-900/30 text-emerald-300' },
  amber: { bg: 'bg-amber-950/20', border: 'border-amber-900/40', text: 'text-amber-400', accent: 'bg-amber-500', dot: 'bg-amber-400', pill: 'bg-amber-950/30 border-amber-900/30 text-amber-300' },
};

/* ─── Preview Components ─────────────────────────────────────────────── */

function IntakePreview() {
  const items = [
    { name: 'Subcontractor List.xlsx', status: 'Parsed', trades: 24, icon: FileText, color: 'text-emerald-400' },
    { name: 'RFI Log — Mechanical.pdf', status: 'Parsed', trades: 18, icon: FileText, color: 'text-blue-400' },
    { name: 'Drawing Set — M-Series.pdf', status: '12 equipment tags found', trades: null, icon: ScanLine, color: 'text-cyan-400' },
    { name: 'CO #7 — VE Deletes.pdf', status: '3 items flagged for removal', trades: null, icon: AlertTriangle, color: 'text-amber-400' },
  ];
  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-secondary/30 rounded-sm border border-border/50 animate-fadeIn" style={{ animationDelay: `${i * 150}ms` }}>
          <item.icon className={`w-4 h-4 ${item.color} shrink-0`} />
          <span className="text-xs text-foreground/80 flex-1 font-medium">{item.name}</span>
          <span className="text-[10px] text-emerald-400 font-mono">{item.status}</span>
          {item.trades && <span className="text-[10px] text-muted-foreground font-mono">{item.trades} trades</span>}
        </div>
      ))}
      <div className="h-1.5 bg-secondary rounded-full overflow-hidden mt-3">
        <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full w-full animate-progressBar" />
      </div>
      <p className="text-[10px] text-muted-foreground text-center">SSE real-time progress tracking</p>
    </div>
  );
}

function DrawingsPreview() {
  const rooms = [
    { label: 'Office 201', confidence: 0.94, color: '#3b82f6' },
    { label: 'Corridor', confidence: 0.98, color: '#6b7280' },
    { label: 'Mech Room 101', confidence: 0.91, color: '#f59e0b' },
    { label: 'Restroom', confidence: 0.96, color: '#8b5cf6' },
    { label: 'Conference 305', confidence: 0.89, color: '#10b981' },
  ];
  return (
    <div className="space-y-2">
      <div className="relative aspect-[16/9] bg-muted rounded-sm border border-border overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-5 grid-rows-3 gap-px p-2">
          {rooms.map((r, i) => (
            <div key={i} className="rounded-sm flex items-center justify-center animate-fadeIn"
              style={{ backgroundColor: r.color + '20', border: `1px solid ${r.color}50`, animationDelay: `${i * 200}ms`, gridColumn: i < 2 ? 'span 2' : 'span 1', gridRow: i < 2 ? 'span 1' : 'span 2' }}>
              <div className="text-center">
                <p className="text-[9px] font-mono text-foreground/80">{r.label}</p>
                <p className="text-[8px] font-mono" style={{ color: r.color }}>{(r.confidence * 100).toFixed(0)}%</p>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-muted-foreground px-1">
        <span>CV Polygon Detection + GPT-4o Classification</span>
        <span className="text-emerald-400">5 rooms detected</span>
      </div>
    </div>
  );
}

function CloseoutPreview() {
  const divisions = [
    { csi: '030000', trade: 'Concrete', folders: '9/9', status: 'complete' },
    { csi: '090000', trade: 'Finishes', folders: '6/9', status: 'partial' },
    { csi: '230000', trade: 'HVAC', folders: '4/9', status: 'partial' },
    { csi: '260000', trade: 'Electrical', folders: '2/9', status: 'gap' },
    { csi: '310000', trade: 'Earthwork', folders: 'N/A', status: 'na' },
  ];
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-[60px_1fr_50px_60px] gap-2 px-2 py-1.5 text-[9px] font-heading uppercase tracking-widest text-muted-foreground">
        <span>CSI</span><span>Trade</span><span>Folders</span><span>Status</span>
      </div>
      {divisions.map((d, i) => (
        <div key={i} className="grid grid-cols-[60px_1fr_50px_60px] gap-2 px-2 py-2 bg-secondary/30 rounded-sm border border-border/50 animate-fadeIn" style={{ animationDelay: `${i * 100}ms` }}>
          <span className="text-[10px] font-mono text-muted-foreground">{d.csi}</span>
          <span className="text-xs text-foreground/80">{d.trade}</span>
          <span className="text-[10px] font-mono text-muted-foreground">{d.folders}</span>
          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded text-center ${
            d.status === 'complete' ? 'bg-emerald-950/40 text-emerald-400' :
            d.status === 'partial' ? 'bg-amber-950/40 text-amber-400' :
            d.status === 'gap' ? 'bg-red-950/40 text-red-400' :
            'bg-secondary text-muted-foreground'
          }`}>
            {d.status === 'complete' ? 'Done' : d.status === 'partial' ? 'In Progress' : d.status === 'gap' ? 'Missing' : 'N/A'}
          </span>
        </div>
      ))}
    </div>
  );
}

function EquipmentPreview() {
  const equipment = [
    { type: 'Exhaust Fan', make: 'Greenheck', model: 'SP-A50', csi: '230000', loc: 'Mech Room 101' },
    { type: 'Water Closet', make: 'Kohler', model: 'K-4325', csi: '220000', loc: 'Restrooms' },
    { type: 'LED Troffer', make: 'Lithonia', model: '2BLT4', csi: '260000', loc: 'Offices/Corridors' },
    { type: 'Air Handling Unit', make: '—', model: '—', csi: '230000', loc: 'Rooftop' },
    { type: 'Fire Alarm Panel', make: '—', model: '—', csi: '280000', loc: 'Lobby' },
  ];
  return (
    <div className="space-y-2">
      {equipment.map((e, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-2 bg-secondary/30 rounded-sm border border-border/50 animate-fadeIn" style={{ animationDelay: `${i * 120}ms` }}>
          <Cpu className="w-3.5 h-3.5 text-violet-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-foreground/80 font-medium">{e.make !== '—' ? `${e.make} ${e.model}` : ''} {e.type}</p>
            <p className="text-[10px] text-muted-foreground">{e.csi} · {e.loc}</p>
          </div>
          {e.make !== '—' ? (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-950/40 border border-emerald-900/40 text-emerald-400">OEM</span>
          ) : (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-amber-950/40 border border-amber-900/40 text-amber-400">Needs Make</span>
          )}
        </div>
      ))}
    </div>
  );
}

function HandoffPreview() {
  const packages = [
    { name: 'Turnover Report.pdf', size: '2.4 MB', icon: FileText, items: '32 assets, 26 divisions, 6 gaps', color: 'text-red-400' },
    { name: 'IWMS Import.xlsx', size: '890 KB', icon: BarChart3, items: '6 sheets — Assets, PM Tasks, Warranties, Parts, Contacts, Divisions', color: 'text-emerald-400' },
    { name: 'Turnover Package.zip', size: '3.3 MB', icon: Package, items: 'PDF + Excel + manifests + README', color: 'text-blue-400' },
  ];
  return (
    <div className="space-y-2">
      {packages.map((p, i) => (
        <div key={i} className="flex items-center gap-3 px-3 py-3 bg-secondary/30 rounded-sm border border-border/50 animate-fadeIn" style={{ animationDelay: `${i * 200}ms` }}>
          <p.icon className={`w-5 h-5 ${p.color} shrink-0`} />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-foreground/80 font-medium">{p.name}</p>
            <p className="text-[10px] text-muted-foreground">{p.items}</p>
          </div>
          <span className="text-[10px] font-mono text-muted-foreground">{p.size}</span>
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground text-center mt-2 italic">One click → owner-ready turnover package</p>
    </div>
  );
}

/* ─── Live AI Document Finder Component ──────────────────────────────── */

function ResultSection({ icon: Icon, title, color, badge, badgeColor, children, delay = 0 }) {
  return (
    <div className="p-4 bg-secondary/20 border border-border/50 rounded-sm space-y-3 animate-fadeIn" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-center gap-2 flex-wrap">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className={`text-xs font-heading uppercase tracking-wider ${color}`}>{title}</span>
        {badge && (
          <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded border ${badgeColor}`}>{badge}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function LiveWarrantyLookup() {
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [customMode, setCustomMode] = useState(false);
  const [customMake, setCustomMake] = useState('');
  const [customModel, setCustomModel] = useState('');
  const [customType, setCustomType] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [phase, setPhase] = useState('');
  const [visibleSections, setVisibleSections] = useState(0);
  const resultRef = useRef(null);

  const runLookup = async () => {
    setLoading(true);
    setResult(null);
    setError(null);
    setVisibleSections(0);

    const eq = customMode
      ? { equipment_type: customType, make: customMake || null, model: customModel || null }
      : { equipment_type: PRESET_EQUIPMENT[selectedPreset].type, make: PRESET_EQUIPMENT[selectedPreset].make, model: PRESET_EQUIPMENT[selectedPreset].model };

    if (!eq.equipment_type) {
      setError('Enter an equipment type');
      setLoading(false);
      return;
    }

    try {
      setPhase('Searching Google for manufacturer documentation...');
      await new Promise(r => setTimeout(r, 1000));
      setPhase('Serper API returning real Google results...');

      const resp = await fetch(`${API_URL}/demo/find-docs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(eq),
      });

      if (resp.status === 429) {
        const data = await resp.json();
        setError(data.detail || 'Rate limited — please wait a few seconds and try again.');
        setLoading(false);
        setPhase('');
        return;
      }

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);

      setPhase('GPT-4o reviewing and verifying each URL...');
      await new Promise(r => setTimeout(r, 600));
      setPhase('Extracting warranty terms and building PM program...');
      await new Promise(r => setTimeout(r, 500));

      const data = await resp.json();
      setResult(data);
      setPhase('');
      setLoading(false);

      // Stagger section reveals for dramatic effect
      setTimeout(() => resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      for (let i = 1; i <= 6; i++) {
        setTimeout(() => setVisibleSections(i), i * 400);
      }
    } catch (e) {
      setError(e.message || 'Lookup failed');
      setPhase('');
      setLoading(false);
    }
  };

  const w = result?.warranty_info || {};
  const o = result?.om_info || {};
  const hasOemLinks = (o.om_manual_links || []).filter(l => l.source === 'serper_google');
  const totalVerified = (w.verified_urls_count || 0) + (o.verified_urls_count || 0);

  return (
    <div className="space-y-4">
      {/* Equipment selector */}
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground">Select Equipment</span>
          <button
            onClick={() => { setCustomMode(!customMode); setResult(null); }}
            className="text-[10px] text-emerald-500 hover:text-emerald-400 ml-auto"
            data-testid="demo-toggle-custom"
          >
            {customMode ? 'Use Presets' : 'Try Your Own'}
          </button>
        </div>

        {!customMode ? (
          <div className="grid grid-cols-2 gap-2">
            {PRESET_EQUIPMENT.map((eq, i) => (
              <button
                key={i}
                onClick={() => { setSelectedPreset(i); setResult(null); setVisibleSections(0); }}
                data-testid={`demo-preset-${i}`}
                className={`text-left px-3 py-2.5 rounded-sm border transition-all text-xs ${
                  selectedPreset === i
                    ? 'bg-emerald-950/30 border-emerald-900/50 text-emerald-300'
                    : 'bg-secondary/30 border-border/50 text-muted-foreground hover:border-border'
                }`}
              >
                <p className="font-medium text-foreground/80">{eq.make} {eq.model}</p>
                <p className="text-[10px] text-muted-foreground">{eq.type}</p>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            <input value={customMake} onChange={e => setCustomMake(e.target.value)} placeholder="Manufacturer"
              data-testid="demo-custom-make"
              className="px-3 py-2 bg-muted/50 border border-border rounded-sm text-xs text-foreground/80 placeholder-muted-foreground focus:outline-none focus:border-emerald-700" />
            <input value={customModel} onChange={e => setCustomModel(e.target.value)} placeholder="Model (optional)"
              data-testid="demo-custom-model"
              className="px-3 py-2 bg-muted/50 border border-border rounded-sm text-xs text-foreground/80 placeholder-muted-foreground focus:outline-none focus:border-emerald-700" />
            <input value={customType} onChange={e => setCustomType(e.target.value)} placeholder="Equipment Type"
              data-testid="demo-custom-type"
              className="px-3 py-2 bg-muted/50 border border-border rounded-sm text-xs text-foreground/80 placeholder-muted-foreground focus:outline-none focus:border-emerald-700" />
          </div>
        )}
      </div>

      {/* Run button */}
      <button
        onClick={runLookup}
        disabled={loading}
        data-testid="demo-run-lookup"
        className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-sm font-heading uppercase tracking-wider text-sm transition-all ${
          loading
            ? 'bg-secondary text-muted-foreground cursor-wait'
            : 'bg-gradient-to-r from-emerald-600 to-emerald-500 text-white hover:from-emerald-500 hover:to-emerald-400 shadow-lg shadow-emerald-950/50'
        }`}
      >
        {loading ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Searching Google + Building PM Program...</>
        ) : (
          <><Play className="w-4 h-4" /> Run Live AI Document Finder</>
        )}
      </button>

      {/* Progress phases */}
      {phase && (
        <div className="flex items-center gap-2 px-3 py-2 bg-emerald-950/20 border border-emerald-900/30 rounded-sm">
          <Loader2 className="w-3.5 h-3.5 text-emerald-400 animate-spin" />
          <p className="text-xs text-emerald-400">{phase}</p>
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 px-3 py-2 bg-red-950/20 border border-red-900/30 rounded-sm">
          <AlertTriangle className="w-3.5 h-3.5 text-red-400" />
          <p className="text-xs text-red-400">{error}</p>
        </div>
      )}

      {/* ─── Results: 5 distinct sections that stagger in ──────────── */}
      {result && (
        <div ref={resultRef} className="space-y-3">

          {/* 1. OEM Documents Found */}
          {visibleSections >= 1 && hasOemLinks.length > 0 && (
            <ResultSection icon={FileText} title="OEM Documents Found" color="text-emerald-400"
              badge={`${hasOemLinks.length} real link${hasOemLinks.length > 1 ? 's' : ''} from Google`}
              badgeColor="bg-emerald-950/40 border-emerald-900/40 text-emerald-500" delay={0}>
              <div className="space-y-1.5">
                {hasOemLinks.map((link, i) => (
                  <a key={i} href={link.url} target="_blank" rel="noopener noreferrer"
                    data-testid={`demo-oem-link-${i}`}
                    className="flex items-center gap-2 px-3 py-2 bg-emerald-950/15 border border-emerald-900/30 rounded-sm text-xs hover:bg-emerald-950/30 transition-colors group">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-emerald-300 font-medium truncate group-hover:text-emerald-200">{link.title}</p>
                      <p className="text-[10px] text-emerald-700 truncate font-mono">{link.url}</p>
                    </div>
                    <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded shrink-0 ${
                      link.confidence === 'high' ? 'bg-emerald-50 dark:bg-emerald-950/50 text-emerald-400' :
                      link.confidence === 'medium' ? 'bg-blue-50 dark:bg-blue-950/50 text-blue-400' : 'bg-secondary text-muted-foreground'
                    }`}>{link.confidence || 'verified'}</span>
                    <ExternalLink className="w-3 h-3 text-emerald-600 shrink-0" />
                  </a>
                ))}
              </div>
              {/* Warranty URLs */}
              <div className="flex gap-2 flex-wrap mt-2">
                {w.product_page_url && (
                  <a href={w.product_page_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-sm bg-blue-950/30 border border-blue-900/40 text-blue-400 hover:bg-blue-50 dark:bg-blue-950/50 transition-colors">
                    <Globe className="w-3 h-3" /> Product Page <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
                {w.spec_sheet_url && (
                  <a href={w.spec_sheet_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-sm bg-cyan-950/30 border border-cyan-900/40 text-cyan-400 hover:bg-cyan-950/50 transition-colors">
                    <FileText className="w-3 h-3" /> Spec Sheet PDF <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
                {w.registration_url && (
                  <a href={w.registration_url} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-sm bg-violet-950/30 border border-violet-900/40 text-violet-400 hover:bg-violet-950/50 transition-colors">
                    <Shield className="w-3 h-3" /> Register Warranty <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </div>
            </ResultSection>
          )}

          {/* 2. Warranty Terms */}
          {visibleSections >= 2 && w.warranty_components?.length > 0 && (
            <ResultSection icon={Shield} title="Warranty Coverage" color="text-primary"
              badge={w.source_type}
              badgeColor={w.source_type === 'OEM' ? 'bg-emerald-950/40 border-emerald-900/40 text-emerald-500' : 'bg-orange-950/40 border-primary/30/40 text-primary'}
              delay={0}>
              {w.warranty_summary && (
                <p className="text-xs text-muted-foreground leading-relaxed">{w.warranty_summary}</p>
              )}
              <div className="space-y-1.5">
                {w.warranty_components.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs px-3 py-2.5 bg-muted/50 rounded-sm border border-border/50">
                    <Shield className="w-3 h-3 text-primary/50 shrink-0" />
                    <span className="text-foreground/80 font-medium flex-1">{c.component}</span>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-mono text-emerald-400">{c.parts_years}yr parts</span>
                      <span className="font-mono text-blue-400">{c.labor_years}yr labor</span>
                      {c.requires_registration && (
                        <span className="text-[9px] text-amber-500 font-mono px-1 py-0.5 rounded bg-amber-950/30 border border-amber-900/30">REG REQUIRED</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              {w.warranty_tips?.length > 0 && (
                <div className="text-[10px] text-muted-foreground space-y-0.5 mt-1">
                  {w.warranty_tips.map((t, i) => <p key={i}>* {t}</p>)}
                </div>
              )}
            </ResultSection>
          )}

          {/* 3. PM Schedule */}
          {visibleSections >= 3 && o.tasks?.length > 0 && (
            <ResultSection icon={CalendarCheck} title="Preventive Maintenance Schedule" color="text-blue-400"
              badge={`${o.tasks.length} tasks generated`}
              badgeColor="bg-blue-950/40 border-blue-900/40 text-blue-400" delay={0}>
              {o.maintenance_notes && (
                <p className="text-xs text-muted-foreground leading-relaxed">{o.maintenance_notes}</p>
              )}
              <div className="space-y-1">
                <div className="grid grid-cols-[1fr_80px_55px_45px] gap-2 px-3 py-1 text-[9px] font-heading uppercase tracking-widest text-muted-foreground">
                  <span>Task</span><span>Frequency</span><span>Priority</span><span>Hours</span>
                </div>
                {o.tasks.map((t, i) => (
                  <div key={i} className="grid grid-cols-[1fr_80px_55px_45px] gap-2 items-center px-3 py-2 bg-muted/50 rounded-sm border border-border/50 text-xs">
                    <span className="text-foreground/80">{t.task}</span>
                    <span className="font-mono text-muted-foreground text-[10px]">{t.frequency}</span>
                    <span className={`text-[9px] font-mono px-1 py-0.5 rounded text-center ${
                      t.priority === 'High' ? 'bg-red-950/40 text-red-400' :
                      t.priority === 'Medium' ? 'bg-amber-950/40 text-amber-400' : 'bg-secondary text-muted-foreground'
                    }`}>{t.priority}</span>
                    <span className="font-mono text-muted-foreground text-[10px] text-right">{t.estimated_hours}h</span>
                  </div>
                ))}
              </div>
            </ResultSection>
          )}

          {/* 4. Critical Spare Parts */}
          {visibleSections >= 4 && o.critical_parts?.length > 0 && (
            <ResultSection icon={Cpu} title="Critical Spare Parts" color="text-rose-400"
              badge={`${o.critical_parts.length} identified`}
              badgeColor="bg-rose-950/40 border-rose-900/40 text-rose-400" delay={0}>
              <div className="space-y-1.5">
                {o.critical_parts.map((p, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 bg-muted/50 rounded-sm border border-border/50 text-xs">
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground/80 font-medium">{p.name}</p>
                      <p className="text-[10px] text-muted-foreground mt-0.5">{p.description}</p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 text-[10px] font-mono">
                      <span className="text-muted-foreground">{p.typical_lifespan_years}yr life</span>
                      <span className="text-amber-400">${p.estimated_cost_min}-${p.estimated_cost_max}</span>
                      <span className={`px-1 py-0.5 rounded ${
                        p.failure_risk === 'High' ? 'bg-red-950/40 text-red-400' :
                        p.failure_risk === 'Medium' ? 'bg-amber-950/40 text-amber-400' : 'bg-secondary text-muted-foreground'
                      }`}>{p.failure_risk} risk</span>
                    </div>
                  </div>
                ))}
              </div>
            </ResultSection>
          )}

          {/* 5. Vendor Procurement Links */}
          {visibleSections >= 5 && (() => {
            const allMaterials = (o.tasks || []).flatMap(t => (t.materials || []).filter(m => m.vendor_links?.length > 0));
            if (allMaterials.length === 0) return null;
            return (
              <ResultSection icon={Link2} title="Vendor Procurement" color="text-amber-400"
                badge={`${allMaterials.length} material${allMaterials.length > 1 ? 's' : ''} with supplier links`}
                badgeColor="bg-amber-950/40 border-amber-900/40 text-amber-400" delay={0}>
                <div className="space-y-2">
                  {allMaterials.slice(0, 5).map((m, i) => (
                    <div key={i} className="px-3 py-2.5 bg-muted/50 rounded-sm border border-border/50">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-xs text-foreground/80 font-medium">{m.name}</span>
                        <span className="text-[10px] font-mono text-amber-400">${m.estimated_cost_min}-${m.estimated_cost_max}</span>
                      </div>
                      <div className="flex gap-1.5 flex-wrap">
                        {m.vendor_links.map((v, vi) => (
                          <a key={vi} href={v.url} target="_blank" rel="noopener noreferrer"
                            className="flex items-center gap-1 px-2 py-0.5 text-[10px] rounded-sm bg-amber-950/20 border border-amber-900/30 text-amber-400 hover:bg-amber-950/40 transition-colors">
                            {v.name} <ExternalLink className="w-2.5 h-2.5" />
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </ResultSection>
            );
          })()}

          {/* 6. Summary scoreboard */}
          {visibleSections >= 6 && (
            <div className="grid grid-cols-4 gap-2 animate-fadeIn">
              <div className="text-center p-3 bg-emerald-950/20 border border-emerald-900/30 rounded-sm">
                <p className="text-xl font-mono font-bold text-emerald-400">{totalVerified}</p>
                <p className="text-[9px] font-heading uppercase tracking-widest text-emerald-700">Verified Links</p>
              </div>
              <div className="text-center p-3 bg-orange-950/20 border border-primary/30 rounded-sm">
                <p className="text-xl font-mono font-bold text-primary">{w.warranty_components?.length || 0}</p>
                <p className="text-[9px] font-heading uppercase tracking-widest text-orange-700">Warranty Terms</p>
              </div>
              <div className="text-center p-3 bg-blue-950/20 border border-blue-900/30 rounded-sm">
                <p className="text-xl font-mono font-bold text-blue-400">{o.tasks?.length || 0}</p>
                <p className="text-[9px] font-heading uppercase tracking-widest text-blue-700">PM Tasks</p>
              </div>
              <div className="text-center p-3 bg-rose-950/20 border border-rose-900/30 rounded-sm">
                <p className="text-xl font-mono font-bold text-rose-400">{o.critical_parts?.length || 0}</p>
                <p className="text-[9px] font-heading uppercase tracking-widest text-rose-700">Spare Parts</p>
              </div>
            </div>
          )}

          {visibleSections >= 6 && (
            <div className="flex items-center justify-center gap-3 px-4 py-3 bg-emerald-950/15 border border-emerald-900/20 rounded-sm animate-fadeIn">
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
              <p className="text-xs text-emerald-500">
                All URLs sourced from Google Search, verified by GPT-4o — <span className="text-emerald-300 font-medium">zero hallucinated links</span>
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Preview Renderer ───────────────────────────────────────────────── */

function PreviewContent({ type }) {
  switch (type) {
    case 'intake': return <IntakePreview />;
    case 'drawings': return <DrawingsPreview />;
    case 'closeout': return <CloseoutPreview />;
    case 'equipment': return <EquipmentPreview />;
    case 'warranty': return <LiveWarrantyLookup />;
    case 'handoff': return <HandoffPreview />;
    default: return null;
  }
}

/* ─── Video Gallery ──────────────────────────────────────────────────── */

const STYLE_ICONS = { 'Screen Recording': MonitorPlay, 'Cinematic': Film, 'Slideshow': Eye };
const STYLE_COLORS = { 'Screen Recording': 'text-cyan-400', 'Cinematic': 'text-violet-400', 'Slideshow': 'text-amber-400' };

function VideoGallery() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [copiedSlug, setCopiedSlug] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/demo/videos`)
      .then(r => r.json())
      .then(data => { setVideos(data.videos || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const copyShareLink = (slug) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/demo/video/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2000);
    });
  };

  if (loading || videos.length === 0) return null;

  return (
    <div className="border-t border-border" data-testid="video-gallery">
      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex items-center gap-3 mb-6">
          <Film className="w-5 h-5 text-violet-400" />
          <h2 className="text-lg font-heading font-bold uppercase tracking-wider text-foreground">Marketing Videos</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-slate-700 to-transparent" />
          <span className="text-[10px] font-mono text-muted-foreground">{videos.length} versions</span>
        </div>

        {/* Video player */}
        {playingUrl && (
          <div className="mb-6 bg-black rounded-sm overflow-hidden border border-border animate-fadeIn">
            <video
              src={`${process.env.REACT_APP_BACKEND_URL}${playingUrl}`}
              controls
              autoPlay
              className="w-full max-h-[500px]"
              data-testid="video-player"
            />
          </div>
        )}

        {/* Grid of videos */}
        <div className="grid grid-cols-3 gap-3">
          {videos.map((v, i) => {
            const Icon = STYLE_ICONS[v.style] || Film;
            const color = STYLE_COLORS[v.style] || 'text-muted-foreground';
            const isPlaying = playingUrl === v.download_url;
            return (
              <div key={i} className={`p-4 rounded-sm border transition-all ${
                isPlaying ? 'bg-violet-950/20 border-violet-900/40' : 'bg-card border-border hover:border-border'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <Icon className={`w-4 h-4 ${color}`} />
                  <span className="text-xs font-heading font-bold uppercase tracking-wider text-foreground">{v.style}</span>
                  <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">{v.length}</span>
                </div>
                <p className="text-[10px] text-muted-foreground mb-3">{v.description}</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPlayingUrl(isPlaying ? null : v.download_url)}
                    data-testid={`video-play-${i}`}
                    className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border transition-all ${
                      isPlaying
                        ? 'bg-violet-600 border-violet-500 text-white'
                        : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground/80 hover:border-slate-600'
                    }`}
                  >
                    <Play className="w-3 h-3" /> {isPlaying ? 'Playing' : 'Play'}
                  </button>
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}${v.download_url}`}
                    download={v.filename}
                    data-testid={`video-download-${i}`}
                    className="flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border bg-muted/50 border-border text-muted-foreground hover:text-foreground/80 hover:border-slate-600 transition-all"
                  >
                    <Download className="w-3 h-3" /> {v.size_mb} MB
                  </a>
                  {v.slug && (
                    <button
                      onClick={(e) => { e.stopPropagation(); copyShareLink(v.slug); }}
                      data-testid={`video-share-${i}`}
                      title="Copy share link"
                      className={`flex items-center gap-1 px-2.5 py-1.5 text-[10px] font-heading uppercase tracking-wider rounded-sm border transition-all ${
                        copiedSlug === v.slug
                          ? 'bg-emerald-950/30 border-emerald-800 text-emerald-400'
                          : 'bg-muted/50 border-border text-muted-foreground hover:text-foreground/80 hover:border-slate-600'
                      }`}
                    >
                      {copiedSlug === v.slug ? <Check className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
                      {copiedSlug === v.slug ? 'Copied' : 'Share'}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ─── Main Demo Page ─────────────────────────────────────────────────── */

export default function DemoPage() {
  const [activeStep, setActiveStep] = useState(4); // Start on warranty lookup
  const navigate = useNavigate();
  const stepRefs = useRef({});

  const scrollToStep = (idx) => {
    setActiveStep(idx);
    stepRefs.current[idx]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="min-h-screen bg-card" data-testid="demo-page">
      {/* Fixed top bar */}
      <div className="sticky top-0 z-50 bg-card/90 backdrop-blur-md border-b border-border/50">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Layers className="w-5 h-5 text-primary" />
            <span className="font-heading font-bold text-sm tracking-wider uppercase text-foreground">
              FMiQ<span className="text-primary">Intel</span>
            </span>
            <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-emerald-950/40 border border-emerald-900/40 text-emerald-400">LIVE DEMO</span>
          </div>
          <button
            onClick={() => navigate('/login')}
            data-testid="demo-login-btn"
            className="flex items-center gap-2 px-4 py-2 text-xs font-heading uppercase tracking-wider bg-orange-600 text-white rounded-sm hover:bg-primary transition-colors"
          >
            Sign In <ArrowRight className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-6 py-12 text-center">
        <h1 className="text-3xl sm:text-4xl font-heading font-bold uppercase tracking-wider text-foreground mb-3">
          See It <span className="text-emerald-400">Live</span>
        </h1>
        <p className="text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          Walk through the entire FMiQ Intel platform — from Day 1 document intake to owner turnover.
          <span className="text-emerald-300 font-medium"> Try the live warranty lookup yourself.</span>
        </p>

        {/* Step nav pills */}
        <div className="flex items-center justify-center gap-2 mt-8 flex-wrap">
          {DEMO_STEPS.map((step, i) => {
            const c = colorMap[step.color];
            const isActive = activeStep === i;
            return (
              <button
                key={step.id}
                onClick={() => scrollToStep(i)}
                data-testid={`demo-nav-${step.id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[11px] font-heading uppercase tracking-wider border transition-all ${
                  isActive
                    ? `${c.bg} ${c.border} ${c.text}`
                    : 'bg-secondary/30 border-border/50 text-muted-foreground hover:text-muted-foreground hover:border-border'
                }`}
              >
                <step.icon className="w-3 h-3" />
                {step.title}
                {step.interactive && <Sparkles className="w-3 h-3 text-emerald-400" />}
              </button>
            );
          })}
        </div>
      </div>

      {/* Steps */}
      <div className="max-w-5xl mx-auto px-6 pb-16">
        <div className="space-y-8">
          {DEMO_STEPS.map((step, i) => {
            const c = colorMap[step.color];
            const isActive = activeStep === i;
            return (
              <div
                key={step.id}
                ref={el => stepRefs.current[i] = el}
                data-testid={`demo-step-${step.id}`}
                className={`relative border rounded-sm overflow-hidden transition-all duration-300 cursor-pointer ${
                  isActive
                    ? `${c.border} ${c.bg}`
                    : 'border-border/50 bg-card hover:border-border'
                }`}
                onClick={() => setActiveStep(isActive ? -1 : i)}
              >
                {/* Accent bar */}
                <div className={`absolute top-0 left-0 w-1 h-full ${c.accent}`} />

                {/* Header */}
                <div className="flex items-center gap-4 px-6 py-4">
                  <div className={`shrink-0 w-8 h-8 rounded-sm flex items-center justify-center ${isActive ? c.bg : 'bg-muted/50'}`}>
                    <step.icon className={`w-4 h-4 ${isActive ? c.text : 'text-muted-foreground'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono ${c.text}`}>0{i + 1}</span>
                      <h3 className="text-sm font-heading font-bold uppercase tracking-wider text-foreground">{step.title}</h3>
                      {step.interactive && (
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-emerald-950/40 border border-emerald-900/40 text-emerald-400 animate-pulse">
                          INTERACTIVE
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{step.subtitle}</p>
                  </div>
                  {isActive ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                </div>

                {/* Content */}
                {isActive && (
                  <div className="px-6 pb-6 pt-1 border-t border-border/30">
                    {step.interactive ? (
                      /* Full-width layout for the interactive step */
                      <div className="space-y-4">
                        <div className="flex flex-wrap items-start gap-4">
                          <p className="text-xs text-muted-foreground leading-relaxed flex-1 min-w-[260px]">{step.description}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {step.features.map((f, fi) => (
                              <span key={fi} className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono rounded-sm border ${c.pill}`}>
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div onClick={e => e.stopPropagation()}>
                          <PreviewContent type={step.preview} />
                        </div>
                      </div>
                    ) : (
                      /* Two-column layout for non-interactive steps */
                      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1.2fr] gap-6">
                        <div className="space-y-4">
                          <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                          <div className="flex flex-wrap gap-1.5">
                            {step.features.map((f, fi) => (
                              <span key={fi} className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono rounded-sm border ${c.pill}`}>
                                {f}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div onClick={e => e.stopPropagation()}>
                          <PreviewContent type={step.preview} />
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Video Gallery */}
      <VideoGallery />

      {/* CTA */}
      <div className="border-t border-border">
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <h2 className="text-xl font-heading font-bold uppercase tracking-wider text-foreground mb-3">
            Ready to close out your next project?
          </h2>
          <p className="text-sm text-muted-foreground mb-6">Day one. Not month six.</p>
          <button
            onClick={() => navigate('/login')}
            data-testid="demo-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-3 font-heading uppercase tracking-wider text-sm bg-orange-600 text-white rounded-sm hover:bg-primary transition-colors shadow-lg shadow-orange-950/50"
          >
            Get Started <ArrowRight className="w-4 h-4" />
          </button>
          <div className="flex items-center justify-center gap-2 mt-8">
            <Layers className="w-4 h-4 text-primary" />
            <span className="font-heading font-bold text-xs tracking-wider uppercase text-muted-foreground">
              FMiQ<span className="text-primary">Intel</span>
            </span>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Built by FMiQ. Powered by AI. Designed for the people who actually close out buildings.</p>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out forwards;
          opacity: 0;
        }
        @keyframes progressBar {
          from { width: 0; }
          to { width: 100%; }
        }
        .animate-progressBar {
          animation: progressBar 2s ease-out forwards;
        }
      `}</style>
    </div>
  );
}
